import { useMutation } from '@tanstack/react-query';
import type { UseMutationOptions } from '@tanstack/react-query';

import {
  useIdempotentMutation,
  type IdempotentMutationContext,
  type UseIdempotentMutationOptions,
} from './useIdempotentMutation';
import {
  useQueryErrorHandler,
  type UseQueryErrorHandlerOptions,
} from './useQueryErrorHandler';

/**
 * Options for factory-created mutation hooks that support error handling
 */
export interface FactoryMutationOptions<TData, TVariables>
  extends Omit<UseMutationOptions<TData, Error, TVariables>, 'mutationFn'> {
  /**
   * Disable automatic error handling (toast notifications + Sentry logging).
   *
   * Only set to `true` when you need specialized error handling patterns
   * (e.g., dialog-actions, parent component handling, testing).
   *
   * @default false (error handler is enabled by default)
   */
  disableErrorHandler?: boolean;

  /**
   * Configuration for the error handler (when enabled).
   * Only applies when `disableErrorHandler` is false/undefined.
   */
  errorHandlerConfig?: UseQueryErrorHandlerOptions;
}

/**
 * Options for factory-created idempotent mutation hooks
 */
export interface FactoryIdempotentMutationOptions<
  TData,
  TError,
  TVariables,
  TContext,
> extends UseIdempotentMutationOptions<TData, TError, TVariables, TContext> {
  /**
   * Disable automatic error handling (toast notifications + Sentry logging).
   *
   * Only set to `true` when you need specialized error handling patterns
   * (e.g., dialog-actions, parent component handling, testing).
   *
   * @default false (error handler is enabled by default)
   */
  disableErrorHandler?: boolean;

  /**
   * Configuration for the error handler (when enabled).
   * Only applies when `disableErrorHandler` is false/undefined.
   */
  errorHandlerConfig?: UseQueryErrorHandlerOptions;
}

/**
 * Generic mutation hook factory with automatic error handling.
 *
 * Creates a custom hook that accepts any async mutation function and
 * automatically integrates standardized error handling (toast + Sentry).
 *
 * **Error Handling:**
 * - Enabled by default with toast notifications and Sentry logging
 * - Disable by passing `disableErrorHandler: true`
 * - Customize by passing `errorHandlerConfig: { showToast: false, ... }`
 *
 * @param mutationFn - The mutation function to execute
 * @returns React hook that wraps the mutation with error handling
 *
 * @example Basic usage (error handler enabled by default)
 * ```ts
 * const useSaveSettings = createApiMutationHook(saveSettingsMutation);
 * // Automatically gets toast + Sentry on error
 * ```
 *
 * @example Disable error handling (for specialized patterns)
 * ```ts
 * const mutation = useSaveSettings({
 *   disableErrorHandler: true,  // No automatic toast/Sentry
 *   onError: (error) => {
 *     // Custom error handling only
 *   }
 * });
 * ```
 *
 * @example Customize error handling
 * ```ts
 * const mutation = useSaveSettings({
 *   errorHandlerConfig: {
 *     showToast: false,  // Disable toast, keep Sentry
 *     sentryTags: { feature: 'settings' }
 *   },
 *   onError: (error) => {
 *     // Additional custom handling
 *   }
 * });
 * ```
 */
export function createApiMutationHook<TData, TVariables>(
  mutationFn: (variables: TVariables) => Promise<TData>
) {
  return (options?: FactoryMutationOptions<TData, TVariables>) => {
    const {
      disableErrorHandler = false,
      errorHandlerConfig,
      onError,
      ...restOptions
    } = options ?? {};

    // Always call the hook (Rules of Hooks compliance)
    const handleError = useQueryErrorHandler(errorHandlerConfig ?? {});

    return useMutation({
      mutationFn,
      onError: (error, variables, onMutateResult, mutationContext) => {
        // Call standardized error handler unless disabled
        if (!disableErrorHandler) {
          handleError(error);
        }

        // Then call user's custom onError (if provided)
        onError?.(error, variables, onMutateResult, mutationContext);
      },
      ...restOptions,
    });
  };
}

/**
 * Idempotent mutation hook factory with automatic error handling.
 *
 * Creates a custom hook for idempotent mutations with automatic idempotency
 * key management and standardized error handling.
 *
 * **Error Handling:**
 * - Enabled by default with toast notifications and Sentry logging
 * - Disable by passing `disableErrorHandler: true`
 * - Customize by passing `errorHandlerConfig: { showToast: false, ... }`
 *
 * **Note:** `useIdempotentMutation` already has some error handling built-in
 * (errorMessage state). This factory adds toast notifications and Sentry logging.
 *
 * @param mutationFn - The idempotent mutation function to execute
 * @returns React hook that wraps the mutation with error handling
 *
 * @example Basic usage (error handler enabled by default)
 * ```ts
 * const useCreateWidget = createIdempotentMutationHook(createWidgetMutation);
 * // Automatically gets toast + Sentry on error
 * ```
 *
 * @example Disable error handling (for specialized patterns)
 * ```ts
 * const mutation = useCreateWidget({
 *   disableErrorHandler: true,  // No automatic toast/Sentry
 *   onError: (error) => {
 *     // Custom error handling only
 *   }
 * });
 * ```
 *
 * @example Customize error handling
 * ```ts
 * const mutation = useCreateWidget({
 *   errorHandlerConfig: {
 *     sentryTags: { feature: 'widgets' }
 *   },
 *   onError: (error) => {
 *     // Additional custom handling (runs after standardized handler)
 *   }
 * });
 * ```
 */
export function createIdempotentMutationHook<
  TData,
  TVariables,
  TError = unknown,
  TContext = unknown,
>(
  mutationFn: (
    variables: TVariables,
    context: IdempotentMutationContext
  ) => Promise<TData>
) {
  return (
    options?: FactoryIdempotentMutationOptions<
      TData,
      TError,
      TVariables,
      TContext
    >
  ) => {
    const {
      disableErrorHandler = false,
      errorHandlerConfig,
      onError,
      ...restOptions
    } = options ?? {};

    // Always call the hook (Rules of Hooks compliance)
    const handleError = useQueryErrorHandler(errorHandlerConfig ?? {});

    return useIdempotentMutation<TData, TVariables, TError, TContext>(
      mutationFn,
      {
        onError: (error, variables, onMutateResult, context) => {
          // Call standardized error handler unless disabled
          if (!disableErrorHandler) {
            handleError(error);
          }

          // Then call user's custom onError (if provided)
          onError?.(error, variables, onMutateResult, context);
        },
        ...restOptions,
      }
    );
  };
}
